import React, { useMemo } from 'react';

import EnsureId from 'psims/react/pages/primary-pages/data-submissions/shared/ensure-id';
import LoadingSubmission from 'psims/react/pages/primary-pages/data-submissions/shared/loading-submission';
import LoadSubmissionFailed from 'psims/react/pages/primary-pages/data-submissions/shared/load-submission-failed';
import useMsoImporterApi, { UseMsoImporterAPI } from './use-mso-importer-api';
import MsoImporterEditor from './mso-importer-editor';
import { useDashboard } from 'psims/react/providers/api/dashboard';
import { SubmissionStatus } from '../../shared/api';

interface MsoImporterProps {
    id: number;
}
type LoadState = 'loading' | 'loaded' | 'error';

// Shared type
type DataSubmissionLite = {
    id: number;
    caseId: string;
    reportingOrganisationId: number;
    reportingOrganisationName: string;
};

// Same logic as refiner
function usePreviousId(
    data: DataSubmissionLite[],
    currentId: number
) {
    return useMemo<number>(() => {
        if (!data?.length) return currentId;

        const current = data.find(d => d.id === currentId);
        if (!current) return currentId;

        const parseCaseId = (caseId: string) => {
            const parts = caseId.split('-');
            if (parts.length !== 3) return null;

            const [type, periodStr, seqStr] = parts;
            const period = parseInt(periodStr, 10);
            const seq = parseInt(seqStr, 10);

            if (isNaN(period) || isNaN(seq)) return null;

            return { type, period, seq };
        };

        const currentParsed = parseCaseId(current.caseId);
        if (!currentParsed) return currentId;

        const previous = data
            .filter(d => {
                const parsed = parseCaseId(d.caseId);
                return (
                    parsed &&
                    parsed.type === currentParsed.type && // 🔥 MSI vs MSR vs STK
                    d.reportingOrganisationId === current.reportingOrganisationId &&
                    (
                        parsed.period < currentParsed.period ||
                        (parsed.period === currentParsed.period && parsed.seq < currentParsed.seq)
                    )
                );
            })
            .sort((a, b) => {
                const pa = parseCaseId(a.caseId)!;
                const pb = parseCaseId(b.caseId)!;
                return pb.period - pa.period || pb.seq - pa.seq;
            })[0];

        return previous?.id ?? currentId;
    }, [data, currentId]);
}

const MsoImporter = (props: MsoImporterProps) => {
    const vm = useVM(props);

    switch(vm.loadState) {
        case 'error':
            return <LoadSubmissionFailed submissionName='MSO importer' />
        case 'loaded':
            return <MsoImporterEditor
                apiCtrl={vm.apiCtrl as PopulatedProps<UseMsoImporterAPI, 'submission'>}
                prevApiCtrl={vm.prevApiCtrl as PopulatedProps<UseMsoImporterAPI, 'submission'>}
            />;
        default:
            return <LoadingSubmission submissionName='MSO importer' />;
    }
};

function useVM(props: MsoImporterProps) {
    const { msoImporterWeekly: rawData } = useDashboard();

    // ✅ Filter + type guard (fix TS error)
    const data: DataSubmissionLite[] = (rawData ?? []).filter(
        (d): d is DataSubmissionLite =>
            typeof d.id === 'number' &&
            typeof d.caseId === 'string' &&
            typeof d.reportingOrganisationId === 'number' &&
            typeof d.reportingOrganisationName === 'string'
    );

    // ✅ New logic (caseId-based)
    const previousId = usePreviousId(data, props.id);

    const apiCtrl = useMsoImporterApi({ id: props.id });
    const prevApiCtrl = useMsoImporterApi({ id: previousId });

    const isLoadedStatus = (status: SubmissionStatus) =>
        status !== 'init' &&
        status !== 'fetching' &&
        status !== 'fetch_failed';

    const bothFetched =
        isLoadedStatus(apiCtrl.loadStatus) &&
        isLoadedStatus(prevApiCtrl.loadStatus);

    const loadState: LoadState =
        apiCtrl.loadStatus === 'fetch_failed' ||
        prevApiCtrl.loadStatus === 'fetch_failed'
            ? 'error'
            : bothFetched
            ? 'loaded'
            : 'loading';

    if (!bothFetched) {
        return {
            apiCtrl: null,
            prevApiCtrl: null,
            loadState,
        };
    }

    if (!apiCtrl.submission || !prevApiCtrl.submission) {
        return {
            apiCtrl: null,
            prevApiCtrl: null,
            loadState: 'error',
        };
    }

    return {
        apiCtrl: apiCtrl as PopulatedProps<UseMsoImporterAPI, 'submission'>,
        prevApiCtrl: prevApiCtrl as PopulatedProps<UseMsoImporterAPI, 'submission'>,
        loadState: 'loaded',
    };
}

const Page = (props: MsoImporterProps) => (
    <EnsureId id={props.id} Component={MsoImporter} />
);

export default Page;